import { useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, RefObject } from 'react'

/**
 * Wo eine aufgeklappte Auswahlliste steht und wie breit sie ist (7.2
 * Abschnitt 54, R1701).
 *
 * **So breit wie ihr längster Eintrag** — mindestens so breit wie das Feld,
 * höchstens bis zum Fensterrand. Der Befund vom 2026-10-14: Über dem Bericht
 * stand im aufgeklappten Organisationselement *„Konzern Holding AG —
 * Konsolidie…“*. Auswahlfeld und Kontoauswahl rechneten ihre Breite bis dahin
 * je für sich aus der Breite des Feldes, und was länger war, wurde gekürzt —
 * an rund vierzig Feldern. Jetzt rechnet es diese eine Stelle.
 *
 * **Sie wächst, solange sie offen ist, und schrumpft nicht.** Wer tippt, engt
 * die Liste ein, wer im Baum aufklappt, macht sie breiter. Schrumpfte sie mit,
 * spränge ihr Rand bei jedem Buchstaben unter der Hand weg.
 */
export type Listenmass = {
  /** Unter diese Breite geht die Liste nicht, auch wenn jeder Eintrag kürzer ist. */
  mindestbreite: number
  /** Die Höhe, über die sie nicht wächst — darüber rollt sie. */
  hoechstens: number
  /** Die Höhe, die sie mindestens bekommt, auch wo wenig Platz ist. */
  wenigstens: number
  /** Ist unter dem Feld weniger Platz als das, klappt sie nach oben auf. */
  kippAb: number
}

type Ort = { links: number; oben: number; breite: number; hoehe: number; rand: number }

/**
 * Der Ort der Liste — gemessen, nicht gerechnet: unter dem Feld, solange
 * darunter Platz ist, sonst darüber; neu gemessen bei jedem Rollen, jeder
 * Größenänderung und jedem neuen Inhalt, weil eine Liste, die neben ihrem
 * Feld stehen bleibt, schlimmer ist als keine.
 *
 * `inhalt` ist, was die Liste gerade zeigt; ändert er sich, wird nachgemessen.
 */
export function useListenort(
  offen: boolean,
  feld: RefObject<HTMLElement | null>,
  rahmen: RefObject<HTMLElement | null>,
  mass: Listenmass,
  inhalt: unknown,
): CSSProperties | null {
  const [ort, setOrt] = useState<Ort | null>(null)
  const groesste = useRef(0)
  const messen = useRef<() => void>(() => {})

  messen.current = () => {
    const r = feld.current?.getBoundingClientRect()
    if (!r) {
      return
    }

    const darunter = window.innerHeight - r.bottom - 8
    const darueber = r.top - 8
    const nachOben = darunter < mass.kippAb && darueber > darunter
    const hoehe = Math.min(mass.hoechstens, Math.max(mass.wenigstens, nachOben ? darueber : darunter))
    const rand = window.innerWidth - 8

    // Die Liste steht mit `max-content` da — was sie misst, ist die Breite
    // ihres längsten Eintrags, und davon wird nichts zurückgegeben.
    groesste.current = Math.max(groesste.current, rahmen.current?.offsetWidth ?? 0)
    const breite = Math.min(Math.max(r.width, mass.mindestbreite, groesste.current), rand)

    const neu: Ort = {
      links: Math.max(4, Math.min(r.left, window.innerWidth - breite - 4)),
      oben: nachOben ? r.top - hoehe - 2 : r.bottom + 2,
      breite,
      hoehe,
      rand,
    }

    setOrt((alt) =>
      alt &&
      alt.links === neu.links &&
      alt.oben === neu.oben &&
      alt.breite === neu.breite &&
      alt.hoehe === neu.hoehe &&
      alt.rand === neu.rand
        ? alt
        : neu,
    )
  }

  useLayoutEffect(() => {
    if (!offen) {
      groesste.current = 0
      setOrt(null)
      return
    }

    const messe = () => messen.current()
    messe()
    window.addEventListener('resize', messe)
    window.addEventListener('scroll', messe, true)
    return () => {
      window.removeEventListener('resize', messe)
      window.removeEventListener('scroll', messe, true)
    }
  }, [offen])

  // Erst wenn die Liste dasteht, lässt sich ihr längster Eintrag messen — und
  // wieder, sobald sie etwas anderes zeigt.
  const steht = ort !== null
  useLayoutEffect(() => {
    if (offen && steht) {
      messen.current()
    }
  }, [offen, steht, inhalt])

  if (!ort) {
    return null
  }

  return {
    left: ort.links,
    top: ort.oben,
    width: 'max-content',
    minWidth: ort.breite,
    maxWidth: ort.rand,
    maxHeight: ort.hoehe,
  }
}
